import { useMemo } from 'react';

type Matcher<T> = (key: keyof T, value: T[keyof T]) => boolean;

/**
 * Split props so we can provide them for different components.
 * @internal
 */
export function useSplitProps<T extends Record<string, unknown>>(
  props: T,
  matchers: Matcher<T>[],
): { matchedProps: Partial<T>; remainingProps: Partial<T> } {
  return useMemo(() => {
    const matchedProps: Partial<T> = {};
    const remainingProps: Partial<T> = {};

    for (const [key, value] of Object.entries(props) as [
      keyof T,
      T[keyof T],
    ][]) {
      const isMatched = matchers.some((matcher) => matcher(key, value));
      if (isMatched) {
        matchedProps[key] = value;
      } else {
        remainingProps[key] = value;
      }
    }

    return { matchedProps, remainingProps };
  }, [props, matchers]);
}
